import {
  useApiPvpApplicationsSectionsRetrieve,
  useApiPvpApplicationsUserApplicationsBulkSaveCreate,
  useApiUserApplicationsAnswersList,
} from "@/api/lctApIComponents";
import { useAppSelector } from "@/hooks/reduxHooks";
import { PVPApplicationFormQuestion } from "../PVPApplicationFormQuestion";
import { z, ZodTypeAny } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";
import gPhoneNumber from "google-libphonenumber";
import { Button } from "@/components/ui/button";
import { useEffect, useRef, useState } from "react";
import { ApplicationAnswerBulkCreate, ApplicationQuestion } from "../../../api/lctApISchemas";
import { DateTime } from "luxon";
import { useUserAppInfo } from "@/hooks/useUserAppInfo";
import { useFormInstances } from "@/hooks/useFormInstances";
import { PhoneNumberUtil } from "google-libphonenumber";
import { useToast } from "@/hooks/use-toast";

// Seperate stash for types removed by Yarn Codegen

export type SubmissionApplicationAnswer = {
  application_question?: string;
  answer_text?: string | string[] | number | Date | undefined;
};

interface IProps {
  pvpId: string;
  appId: string;
  sectionId: string;
  userAppId: string;
  fieldsLocked?: boolean;
  formInstances?: ReturnType<typeof useFormInstances>;
}
export interface AnswersObject {
  id: string;
  application_question: string;
  answer_text: string | string[] | number | Date;
}

export const formatDefaultValue: (
  type: string | undefined,
  storedValue?: AnswersObject["answer_text"] | null,
) => string | string[] | number | Date | undefined = (type, storedValue) => {
  switch (type) {
    case "MC":
      return Array.isArray(storedValue)
        ? storedValue
        : typeof storedValue === "string"
          ? storedValue.split(",").map((id) => id.trim())
          : [];
    case "DT":
    case "DATE":
      return storedValue ? new Date(storedValue as string) : undefined;
    default:
      return storedValue || "";
  }
};

const phoneUtil = PhoneNumberUtil.getInstance();

const createRequiredValidation = (message?: string) => {
  const errorMessage = message || `This field is required`;
  return (type: z.ZodTypeAny) => {
    return type.refine((val) => val !== undefined && val !== null && val !== "", {
      message: errorMessage,
    });
  };
};

const createNumberValidation = (question: ApplicationQuestion) => {
  const baseValidation = z.preprocess(
    (val) => {
      // Check for empty strings, null, or undefined before coercion
      if (val === "" || val === null || val === undefined) {
        return undefined;
      }
      return val;
    },
    z.coerce
      .number({ message: "This needs to be a number" })
      .min(question.min_value ?? 0)
      .max(question.max_value ?? 999999),
  );

  if (!question.required) {
    return baseValidation.nullable().optional();
  }

  return baseValidation;
};

const createPhoneValidation = () => {
  const baseValidation = z
    .string()
    .refine((value) => value === undefined || value.trim() === "" || phoneUtil.isPossibleNumber(phoneUtil.parse(value, "US")), {
      message: "Invalid phone number",
    });

  return baseValidation.transform((value) => {
    if (!value || value.trim() === "") {
      return "";
    }
    try {
      const phoneNumber = phoneUtil.parseAndKeepRawInput(value, "US");
      return phoneUtil.format(phoneNumber, gPhoneNumber.PhoneNumberFormat.E164);
    } catch {
      return value;
    }
  });
};

export const PVPApplicationFormSection: React.FC<IProps> = ({ pvpId, appId, sectionId, userAppId, fieldsLocked, formInstances }) => {
  const token = useAppSelector((state) => state.user.authKey);
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [latestUpdatedTime, setLatestUpdatedTime] = useState<string>("");
  const [sectionAnswers, setSectionAnswers] = useState<Record<string, AnswersObject>>({});
  const { refetchUserAppData, currentPart } = useUserAppInfo(userAppId);

  const { data: section } = useApiPvpApplicationsSectionsRetrieve(
    { headers: { Authorization: `Token ${token}` }, pathParams: { pvp: pvpId, app: appId, id: sectionId } },
    { staleTime: 1000 },
  );

  const object =
    section?.questions.reduce(
      (acc, question) => {
        const storedValue = sectionAnswers[question.id]?.answer_text;
        const isRequired = question.required;
        const requiredErrorMessage = isRequired ? `This field is required` : "";

        let type: z.ZodTypeAny;
        switch (question.type) {
          case "TEXT":
          case "TA":
          case "BOOLEAN":
            type = isRequired ? z.string().min(1, requiredErrorMessage) : z.string();
            break;
          case "MC":
            type = isRequired
              ? z
                  .array(z.string())
                  .nonempty(requiredErrorMessage)
                  .transform((arr) => arr.filter((val) => val.trim() !== ""))
                  .refine((arr) => arr.length > 0, { message: requiredErrorMessage })
              : z.array(z.string()).transform((arr) => arr.filter((val) => val.trim() !== ""));
            break;
          case "NUMBER":
          case "INTEGER":
            type = createNumberValidation(question);
            if (!isRequired) {
              type = type.optional();
            }
            break;
          case "DT":
          case "DATE":
            if (isRequired) {
              type = z.date({
                message: "Please select a valid date",
              });
            } else {
              // Preprocess to handle empty/invalid values for non-required dates
              type = z.preprocess((val) => {
                if (val === "" || val === null || val === undefined) return undefined;
                if (val instanceof Date && !isNaN(val.getTime())) return val;
                return undefined;
              }, z.date().optional());
            }
            break;
          case "EMAIL":
            type = isRequired ? z.string().email(requiredErrorMessage) : z.string().email();
            break;
          case "PHONE":
            type = createPhoneValidation();
            if (!isRequired) {
              type = type.optional();
            }
            break;
          case "UPLOAD":
            type = isRequired ? z.string().min(1, "A file is required") : z.string();
            break;
          default:
            type = isRequired ? z.string().min(1, requiredErrorMessage) : z.string();
        }

        if (question.required && !storedValue && !fieldsLocked) {
          type = createRequiredValidation(requiredErrorMessage)(type);
        }

        acc[question.id] = type;
        return acc;
      },
      {} as Record<string, ZodTypeAny>,
    ) ?? {};

  const formSchema = z.object(object);
  const defaultValues = Object.fromEntries(
    section?.questions.map((question) => [
      question.id,
      question.type === "MC" ? sectionAnswers[question.id]?.answer_text || [] : sectionAnswers[question.id]?.answer_text || "",
    ]) ?? [],
  );

  // SAVE INDIVIDUAL SECTIONS HOOK

  const saveSection = useApiPvpApplicationsUserApplicationsBulkSaveCreate({
    onSuccess: (data) => {
      console.log("Section updated successfully:", data);
    },
    onError: (error) => {
      console.error("Failed to update section:", error);
    },
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: "all",
    defaultValues,
    reValidateMode: "onBlur",
    shouldFocusError: true,
  });

  const saveHandlerRef = useRef<() => Promise<void> | void>(() => {});

  // FETCH THE CURRENT VALUES OF THE USER APP SECTION ANSWERS FROM THE BACK END

  const { data: userAnswersFromApi } = useApiUserApplicationsAnswersList(
    { headers: { Authorization: `Token ${token}` }, pathParams: { id: userAppId } },
    { staleTime: 1000 },
  );

  // THIS CHECKS IF THE BACK END SECTION ANSWERS OBJECT CHANGES WHEN SAVE SECTION IS PRESSED, OR ANSWER DELETED THEN THIS UPDATES FRONT END FORM VALUES
  useEffect(() => {
    if (userAnswersFromApi && userAnswersFromApi?.length > 0) {
      const answersMap = userAnswersFromApi.reduce(
        (acc: Record<string, AnswersObject>, answer) => {
          acc[answer.application_question] = {
            id: answer.id || "",
            application_question: answer.application_question,
            answer_text: answer.answer_text ? answer.answer_text : "",
          };
          return acc;
        },
        {} as Record<string, AnswersObject>,
      );

      section?.questions.forEach((question) => {
        const answerText = answersMap[question.id]?.answer_text || "";
        const selectedOptionIds = typeof answerText === "string" ? answerText.split(",").map((id) => id.trim()) : [];
        question.answer_options.forEach((option) => {
          option.is_selected = selectedOptionIds.includes(option.id);
        });
      });

      setSectionAnswers(answersMap);

      // Reset form with values from API
      const apiValues = Object.fromEntries(
        section?.questions.map((question) => [question.id, formatDefaultValue(question.type, answersMap[question.id]?.answer_text)]) ?? [],
      );
      form.reset(apiValues, { keepDirtyValues: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userAnswersFromApi, section?.questions]);
  // THIS FUNCTION IS CALLED WHEN THE SAVE SECTION BUTTON IS PRESSED - WE PASSED IN ALL INPUT VALUES AS AN ANSWERS OBJECT AND ONCE SAVE OR UPDATE RESPONSE IS CONFIRMED AS SUCCESSFUL WE CHECK TO SEE IF ALL REQUIRED FIELDS HAVE BEEN COMPLETED TO UPDATE FORM SUBMISSION STATUS
  async function onSubmit(values: z.infer<typeof formSchema>) {
    if (!section || section.part !== currentPart) return;
    const formAnswers: SubmissionApplicationAnswer[] = [];

    const bulkSavePayload = { answers: formAnswers };
    const now = DateTime.now().toFormat("yyyy-MM-dd HH:mm:ss");
    section?.questions.forEach((question) => {
      const storedValue = sectionAnswers[question.id]?.answer_text;
      const rawValue = values[question.id] as unknown;
      let currentValue: string | undefined;

      if (question.type === "MC") {
        const validValues = Array.isArray(rawValue) ? rawValue.filter((val) => val.trim() !== "") : [];
        currentValue = validValues.length > 0 ? validValues.join(",") : undefined;
      } else if (typeof rawValue === "number") {
        currentValue = rawValue.toString();
      } else if (typeof rawValue === "string") {
        currentValue = rawValue;
      } else if (rawValue instanceof Date) {
        currentValue = DateTime.fromJSDate(rawValue).toFormat("yyyy-MM-dd HH:mm:ss");
      } else {
        currentValue = undefined;
      }

      if (question.type === "PHONE" && currentValue !== undefined) {
        currentValue = currentValue.toString();
      }

      const answerText = currentValue !== undefined && currentValue !== storedValue ? currentValue : storedValue;
      formAnswers.push({
        application_question: question.id,
        answer_text: answerText !== undefined ? answerText.toString() : "",
      });
    });
    try {
      setLoading(true);
      const savedResponse = await saveSection.mutateAsync({
        headers: { Authorization: `Token ${token}` },
        body: bulkSavePayload as unknown as ApplicationAnswerBulkCreate,
        pathParams: { pvp: pvpId, app: appId, id: userAppId },
      });
      if (savedResponse) {
        try {
          await refetchUserAppData();
        } catch (error) {
          toast({
            title: "Error",
            description: "Failed to refetch user app data: " + (error instanceof Error ? error.message : "Unknown error"),
            variant: "destructive",
            duration: 5000,
          });
          console.error("Error refetching user app data:", error);
        }
        setLatestUpdatedTime(now);
        setSectionAnswers((prevAnswers) => {
          const updatedAnswers = { ...prevAnswers };
          formAnswers.forEach((answer) => {
            if (answer?.application_question) {
              updatedAnswers[answer.application_question] = {
                ...updatedAnswers[answer.application_question],
                answer_text: answer.answer_text ?? "",
              };
            }
          });
          return updatedAnswers;
        });

        // Reset form with current values as the new baseline
        form.reset(form.getValues());
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save the section: " + (error instanceof Error ? error.message : "Unknown error"),
        variant: "destructive",
        duration: 5000,
      });
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  }

  saveHandlerRef.current = () => form.handleSubmit(onSubmit)();

  useEffect(() => {
    if (formInstances) {
      const unregister = formInstances.registerForm(form, () => saveHandlerRef.current());
      return () => unregister();
    }
  }, [form, formInstances]);
  if (!section?.id) return null;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="mx-auto mb-4 h-full flex-grow pt-6">
        <section
          id={`section-${section.id}`}
          className="relative mb-1 space-y-4 overflow-auto rounded-lg border border-slate-300 bg-white p-6 pb-0"
        >
          <span className="align-bottom text-lg font-semibold">
            <h3>{`${section?.order}. ${section?.header_text}`}</h3>
          </span>
          {section?.questions.map((question) => (
            <Controller
              key={question.id}
              name={question.id as keyof z.infer<typeof formSchema>}
              control={form.control}
              defaultValue={sectionAnswers[question.id]?.answer_text || ""}
              render={({ field }) => (
                <PVPApplicationFormQuestion
                  question={question}
                  fieldsLocked={fieldsLocked}
                  {...field}
                  storedValue={sectionAnswers[question.id]?.answer_text ?? ""}
                  userAppId={userAppId}
                />
              )}
            />
          ))}
          <div className="flex flex-row py-6 sm:flex-wrap">
            {latestUpdatedTime ? (
              <div className="mr-2 flex w-auto flex-row items-center text-wrap align-middle md:text-nowrap">
                <div className="text-sm text-slate-800">Last updated</div>
                <div className="ml-2 items-center align-middle text-xs font-thin text-gray-600 sm:text-sm">{latestUpdatedTime}</div>
              </div>
            ) : null}
            <div className="flex flex-grow justify-end">
              <Button type="submit" disabled={loading || fieldsLocked} loading={loading}>
                Save Section
              </Button>
            </div>
          </div>
        </section>
      </form>
    </Form>
  );
};
